import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, memo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, MessageCircle, Reply, Trash2, ThumbsUp, ThumbsDown, MoreVertical, Edit2, Share2, Flag, ChevronRight, Send } from 'lucide-react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { topics } from '@/data/videoTopics';
import VideoShareButton from '@/components/VideoShareButton';
import { MentionTextarea } from '@/components/MentionTextarea';
import { parseMentions, extractMentions } from '@/utils/mentionParser';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Video {
  id: number;
  title?: string;
  embedCode: string;
}

interface Comment {
  id: string;
  username: string;
  content: string;
  created_at: string;
  parent_comment_id: string | null;
  user_id: string;
  likes?: number;
  dislikes?: number;
  userReaction?: 'like' | 'dislike' | null;
  avatar_url?: string;
}

// Helper function to check if video is vertical
const isVerticalVideo = (video: Video | null): boolean => {
  if (!video) return false;
  const paddingMatch = video.embedCode.match(/padding:([^;]+)/);
  const aspectRatio = paddingMatch ? paddingMatch[1] : '56.25% 0 0 0';
  const paddingPercentage = parseFloat(aspectRatio);
  return paddingPercentage > 100;
};

// Memoized video player component to prevent re-renders when typing comments
const VideoPlayer = memo(({ video }: { video: Video | null }) => {
  const isMobile = useIsMobile();
  
  if (!video) {
    return (
      <div className="aspect-video bg-muted rounded-lg mb-8 flex items-center justify-center">
        <p className="text-muted-foreground">Video not found</p>
      </div>
    );
  }

  const paddingMatch = video.embedCode.match(/padding:([^;]+)/);
  const aspectRatio = paddingMatch ? paddingMatch[1] : '56.25% 0 0 0';
  
  // Extract padding percentage to determine if video is vertical
  const paddingPercentage = parseFloat(aspectRatio);
  const isVertical = paddingPercentage > 100; // Vertical videos have padding > 100%

  // Add autoplay, unmuted, and playsinline parameters to the embed code
  const autoplayEmbedCode = video.embedCode.replace(
    /src="([^"]+)"/,
    (match, url) => {
      const separator = url.includes('?') ? '&' : '?';
      return `src="${url}${separator}autoplay=1&muted=0&playsinline=1"`;
    }
  );

  // For vertical videos on desktop, constrain height and center horizontally
  const containerStyles = !isMobile && isVertical
    ? {
        maxHeight: 'calc(100vh - 200px)', // Fit within viewport with some padding
        width: 'auto',
        margin: '0 auto',
        aspectRatio: `${100 / paddingPercentage}`, // Calculate proper aspect ratio
      }
    : {};

  return (
    <div className={`${!isMobile && isVertical ? '' : 'mb-8'} w-full flex justify-center`}>
      <div 
        className={`bg-muted rounded-lg relative ${
          !isMobile && isVertical 
            ? 'h-[calc(100vh-200px)]' 
            : 'w-full max-h-[600px]'
        } overflow-hidden`}
        style={!isMobile && isVertical ? containerStyles : { padding: aspectRatio, width: '100%' }}
      >
        <div 
          className="absolute top-0 left-0 w-full h-full"
          dangerouslySetInnerHTML={{ __html: autoplayEmbedCode }}
        />
      </div>
    </div>
  );
});

// Comments Section Component (outside main component to prevent re-creation on each render)
const CommentsSection = memo(({ 
  topLevelComments, 
  totalComments,
  user, 
  newComment, 
  setNewComment, 
  isLoading, 
  handleAddComment, 
  navigate, 
  renderComment 
}: { 
  topLevelComments: Comment[]; 
  totalComments: number;
  user: any; 
  newComment: string; 
  setNewComment: (value: string) => void; 
  isLoading: boolean; 
  handleAddComment: () => void; 
  navigate: any; 
  renderComment: (comment: Comment) => React.ReactNode;
}) => (
  <div className="space-y-6">
    <h2 className="text-lg font-semibold mb-2">
      Comments ({totalComments})
    </h2>

    {/* Add Comment Form */}
    {user ? (
      <div className="pb-4 border-b border-border/40">
        <MentionTextarea
          placeholder="Share your thoughts... (type @ to mention someone)"
          value={newComment}
          onChange={setNewComment}
          className="min-h-[80px] mb-3 text-sm border-2 border-gold shadow-[0_0_15px_rgba(212,175,55,0.3)] focus-within:border-gold focus-within:shadow-[0_0_20px_rgba(212,175,55,0.5)]"
        />
        {newComment.trim() && (
          <div className="flex justify-end">
            <Button
              onClick={handleAddComment}
              disabled={isLoading}
              size="sm"
              className="h-8 px-4 rounded-full"
            >
              Comment
            </Button>
          </div>
        )}
      </div>
    ) : (
      <div className="py-6 text-center border-b border-border/40">
        <p className="text-muted-foreground mb-4 text-sm">
          Please sign in to leave a comment
        </p>
        <Button onClick={() => navigate('/auth')} size="sm">
          Sign In
        </Button>
      </div>
    )}

    {/* Comments List */}
    <div>
      {topLevelComments.map(comment => renderComment(comment))}
      {topLevelComments.length === 0 && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          No comments yet. Be the first to comment!
        </p>
      )}
    </div>
  </div>
));

const VideoDetailPage = () => {
  const { videoId, categoryId, videoSlug } = useParams<{ videoId?: string; categoryId?: string; videoSlug?: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const [video, setVideo] = useState<Video | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const loadVideo = async () => {
      let foundVideo: Video | null = null;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

      // New format: separate route params for category and video slug
      if (categoryId && videoSlug) {
        const uuidMatch = videoSlug.match(uuidRegex);
        if (uuidMatch) {
          // DB-backed video (UUID id)
          const { data } = await supabase
            .from('videos')
            .select('id, title, embed_code')
            .eq('id', uuidMatch[0])
            .maybeSingle();
          if (data) {
            foundVideo = { id: data.id as any, title: data.title, embedCode: data.embed_code } as unknown as Video;
          }
        } else {
          // Legacy numeric static id at start of slug
          const videoNumber = videoSlug.split('-')[0];
          for (const topic of topics) {
            if (topic.id === categoryId) {
              const videoMatch = topic.videos.find(v => String(v.id) === videoNumber);
              if (videoMatch) {
                foundVideo = videoMatch;
                break;
              }
            }
          }
        }
      } else if (videoId) {
        // Legacy format: "category-id-videoNumber" or just "videoNumber"
        const parts = videoId.split('-');
        let legacyCategoryId: string | null = null;
        let videoNumber: string | null = null;

        if (parts.length >= 2) {
          videoNumber = parts[parts.length - 1];
          legacyCategoryId = parts.slice(0, -1).join('-');
        } else {
          videoNumber = videoId;
        }

        for (const topic of topics) {
          if (legacyCategoryId && topic.id !== legacyCategoryId) continue;
          const videoMatch = topic.videos.find(v => String(v.id) === videoNumber);
          if (videoMatch) {
            foundVideo = videoMatch;
            break;
          }
        }
      }

      if (!cancelled) setVideo(foundVideo);
    };
    loadVideo();
    return () => { cancelled = true; };
  }, [videoId, categoryId, videoSlug]);


  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
    fetchComments();
  }, [user, videoId]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('public_profiles')
      .select('username')
      .eq('id', user.id)
      .single();

    if (data?.username) {
      setUsername(data.username);
    }
  };

  const fetchComments = async () => {
    // Use the composite ID for new format or videoId for legacy
    const commentVideoId = categoryId && videoSlug ? `${categoryId}/${videoSlug}` : videoId;
    
    const { data: commentsData, error } = await supabase
      .from('video_comments')
      .select('*')
      .eq('video_id', commentVideoId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching comments:', error);
      return;
    }

    // Fetch reactions for all comments
    const { data: reactionsData } = await supabase
      .from('comment_reactions')
      .select('comment_id, reaction_type, user_id');

    // Fetch avatar URLs for all comment authors from public_profile_data
    const userIds = [...new Set(commentsData?.map(c => c.user_id) || [])];
    const { data: profilesData, error: profilesError } = await supabase
      .from('public_profile_data')
      .select('id, avatar_url')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
    }

    // Create a map of user_id to avatar_url with cache busting
    const avatarMap = new Map(
      profilesData?.map(p => [
        p.id, 
        p.avatar_url ? `${p.avatar_url.split('?')[0]}?t=${Date.now()}` : ''
      ]) || []
    );

    // Process comments with reaction counts and avatars
    const processedComments = (commentsData || []).map(comment => {
      const commentReactions = reactionsData?.filter(r => r.comment_id === comment.id) || [];
      const likes = commentReactions.filter(r => r.reaction_type === 'like').length;
      const dislikes = commentReactions.filter(r => r.reaction_type === 'dislike').length;
      const userReaction = user 
        ? commentReactions.find(r => r.user_id === user.id)?.reaction_type as 'like' | 'dislike' | undefined
        : null;

      const userAvatarUrl = avatarMap.get(comment.user_id) || '';

      return {
        ...comment,
        likes,
        dislikes,
        userReaction: userReaction || null,
        avatar_url: userAvatarUrl
      };
    });

    setComments(processedComments);
  };

  const handleAddComment = async () => {
    if (!user) {
      toast.error('Please sign in to comment');
      navigate('/auth');
      return;
    }

    if (!username) {
      toast.error('Please set a username in your profile');
      return;
    }

    if (!newComment.trim()) return;

    setIsLoading(true);

    // Use the composite ID for new format or videoId for legacy
    const commentVideoId = categoryId && videoSlug ? `${categoryId}/${videoSlug}` : videoId;

    // Insert comment
    const { data: commentData, error } = await supabase
      .from('video_comments')
      .insert({
        video_id: commentVideoId,
        user_id: user.id,
        username: username,
        content: newComment.trim(),
        parent_comment_id: null,
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to add comment');
      console.error(error);
      setIsLoading(false);
      return;
    }

    // Process mentions
    const mentions = extractMentions(newComment);
    if (mentions.length > 0 && commentData) {
      await processMentions(mentions, commentData.id);
    }

    toast.success('Comment added');
    setNewComment('');
    fetchComments();
    setIsLoading(false);
  };

  const handleAddReply = async (parentId: string) => {
    if (!user) {
      toast.error('Please sign in to reply');
      navigate('/auth');
      return;
    }

    if (!username) {
      toast.error('Please set a username in your profile');
      return;
    }

    if (!replyContent.trim()) return;

    setIsLoading(true);

    // Use the composite ID for new format or videoId for legacy
    const commentVideoId = categoryId && videoSlug ? `${categoryId}/${videoSlug}` : videoId;

    // Insert reply
    const { data: replyData, error } = await supabase
      .from('video_comments')
      .insert({
        video_id: commentVideoId,
        user_id: user.id,
        username: username,
        content: replyContent.trim(),
        parent_comment_id: parentId,
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to add reply');
      console.error(error);
      setIsLoading(false);
      return;
    }

    // Process mentions
    const mentions = extractMentions(replyContent);
    if (mentions.length > 0 && replyData) {
      await processMentions(mentions, replyData.id);
    }

    toast.success('Reply added');
    setReplyContent('');
    setReplyTo(null);
    fetchComments();
    setIsLoading(false);
  };

  const processMentions = async (mentions: string[], commentId: string) => {
    // Get user IDs for mentioned usernames
    const { data: users } = await supabase
      .from('public_profile_data')
      .select('id, username')
      .in('username', mentions);

    if (users && users.length > 0) {
      // Insert mention records
      const mentionRecords = users.map(user => ({
        comment_id: commentId,
        mentioned_user_id: user.id,
        mentioned_username: user.username,
      }));

      await supabase
        .from('comment_mentions')
        .insert(mentionRecords);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    const { error } = await supabase
      .from('video_comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      toast.error('Failed to delete comment');
    } else {
      toast.success('Comment deleted');
      fetchComments();
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editingContent.trim()) return;
    
    setIsLoading(true);
    
    const { error } = await supabase
      .from('video_comments')
      .update({ content: editingContent.trim() })
      .eq('id', commentId);
    
    if (error) {
      toast.error('Failed to update comment');
    } else {
      toast.success('Comment updated');
      setEditingCommentId(null);
      setEditingContent('');
      fetchComments();
    }
    
    setIsLoading(false);
  };

  const handleShareComment = async (commentId: string) => {
    const commentUrl = `${window.location.origin}${window.location.pathname}#comment-${commentId}`;
    
    try {
      await navigator.clipboard.writeText(commentUrl);
      toast.success('Comment link copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleReportComment = async (commentId: string, commentUsername: string) => {
    // In a real app, this would send a report to moderators
    // For now, we'll just show a success message
    toast.success(`Comment by @${commentUsername} has been reported. Our team will review it.`);
  };

  const handleReaction = async (commentId: string, reactionType: 'like' | 'dislike') => {
    if (!user) {
      toast.error('Please sign in to react');
      navigate('/auth');
      return;
    }

    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;

    // If user already has this reaction, remove it
    if (comment.userReaction === reactionType) {
      const { error } = await supabase
        .from('comment_reactions')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id);

      if (error) {
        toast.error('Failed to remove reaction');
      } else {
        fetchComments();
      }
      return;
    }

    // Delete existing reaction if switching
    await supabase
      .from('comment_reactions')
      .delete()
      .eq('comment_id', commentId)
      .eq('user_id', user.id);

    // Add new reaction
    const { error } = await supabase
      .from('comment_reactions')
      .insert({
        comment_id: commentId,
        user_id: user.id,
        reaction_type: reactionType,
      });

    if (error) {
      toast.error('Failed to add reaction');
    } else {
      fetchComments();
    }
  };

  const renderComment = (comment: Comment, depth: number = 0) => {
    const replies = comments.filter(c => c.parent_comment_id === comment.id);
    const isEditing = editingCommentId === comment.id;
    const isExpanded = expandedReplies.has(comment.id);
    const isReply = depth > 0;
    
    // Only indent first-level replies (depth 1), not deeper ones
    const indentClass = depth === 1 ? 'ml-8 sm:ml-12' : '';
    
    // Get the most recent reply for showing its avatar
    const mostRecentReply = replies.length > 0 
      ? replies.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null;

    const toggleReplies = () => {
      setExpandedReplies(prev => {
        const newSet = new Set(prev);
        if (newSet.has(comment.id)) {
          newSet.delete(comment.id);
        } else {
          newSet.add(comment.id);
        }
        return newSet;
      });
    };

    return (
      <div 
        key={comment.id} 
        id={`comment-${comment.id}`} 
        className={`${indentClass} py-3 ${isReply ? '' : 'border-b border-border/40'}`}
      >
        <div className="flex gap-2 sm:gap-3">
          {/* User Avatar with vertical line for replies */}
          <div className="relative flex-shrink-0">
            <Link to={user?.id === comment.user_id ? '/profile' : `/user/${comment.username}`}>
              <Avatar className="h-8 w-8 sm:h-10 sm:w-10 relative z-10" key={`${comment.user_id}-${comment.avatar_url}`}>
                <AvatarImage 
                  src={comment.avatar_url || ''} 
                  className="object-cover"
                  alt={`${comment.username}'s avatar`}
                />
                <AvatarFallback className="bg-muted text-xs">
                  {comment.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </Link>
            {/* Curved line connector - only show for top-level comments with replies */}
            {!isReply && replies.length > 0 && (
              <div className="absolute left-1/2 top-10 sm:top-12 w-8 h-12">
                {/* Vertical line */}
                <div className="absolute left-0 top-0 w-0.5 h-8 bg-border" />
                {/* Curved corner */}
                <div className="absolute left-0 top-8 w-8 h-4 border-l-2 border-b-2 border-border rounded-bl-lg" />
              </div>
            )}
          </div>

          {/* Comment Content */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2 mb-1">
              <div className="flex-1 min-w-0 flex items-baseline gap-1 sm:gap-2 flex-wrap">
                <Link 
                  to={user?.id === comment.user_id ? '/profile' : `/user/${comment.username}`}
                  className="text-sm font-medium hover:underline"
                >
                  @{comment.username}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {new Date(comment.created_at).toLocaleDateString()}
                </span>
              </div>
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background border z-50">
                    {user.id === comment.user_id && (
                      <>
                        <DropdownMenuItem onClick={() => {
                          setEditingCommentId(comment.id);
                          setEditingContent(comment.content);
                        }}>
                          <Edit2 className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => handleShareComment(comment.id)}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </DropdownMenuItem>
                    {user.id !== comment.user_id && (
                      <DropdownMenuItem onClick={() => handleReportComment(comment.id, comment.username)}>
                        <Flag className="mr-2 h-4 w-4" />
                        Report
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2 mb-2">
                <Textarea
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  className="min-h-[60px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleEditComment(comment.id)}
                    disabled={isLoading}
                    className="h-8 text-xs"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditingContent('');
                    }}
                    className="h-8 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm mb-2 break-words">
                {parseMentions(comment.content, username)}
              </p>
            )}
            <div className="flex items-center gap-1 sm:gap-3 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReaction(comment.id, 'like')}
                className={`h-7 px-2 text-xs ${comment.userReaction === 'like' ? 'text-primary' : ''}`}
              >
                <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">{comment.likes || 0}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReaction(comment.id, 'dislike')}
                className={`h-7 px-2 text-xs ${comment.userReaction === 'dislike' ? 'text-destructive' : ''}`}
              >
                <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">{comment.dislikes || 0}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                className="h-7 px-2 text-xs"
              >
                <Reply className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Reply</span>
              </Button>
            </div>
            {replyTo === comment.id && (
              <div className="mt-2 space-y-2">
                <MentionTextarea
                  placeholder="Write your reply... (type @ to mention someone)"
                  value={replyContent}
                  onChange={setReplyContent}
                  className="min-h-[60px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAddReply(comment.id)}
                    disabled={isLoading}
                    size="icon"
                    className="rounded-full h-8 w-8"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReplyTo(null);
                      setReplyContent('');
                    }}
                    size="sm"
                    className="h-8 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {replies.length > 0 && mostRecentReply && (
              <div className="mt-3 flex items-start gap-2 sm:gap-3">
                {/* Avatar of most recent replier */}
                <Link to={user?.id === mostRecentReply.user_id ? '/profile' : `/user/${mostRecentReply.username}`}>
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    <AvatarImage 
                      src={mostRecentReply.avatar_url || ''} 
                      className="object-cover"
                      alt={`${mostRecentReply.username}'s avatar`}
                    />
                    <AvatarFallback className="bg-muted text-xs">
                      {mostRecentReply.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <Collapsible open={isExpanded} onOpenChange={toggleReplies} className="flex-1">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-primary hover:text-primary/80 font-medium"
                    >
                      {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                      <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-0 mt-2">
                    <div className="-ml-8 sm:-ml-9">
                      {replies.map(reply => renderComment(reply, depth + 1))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const topLevelComments = comments.filter(c => !c.parent_comment_id);
  const isVertical = isVerticalVideo(video);
  const showSideLayout = !isMobile && isVertical;

  return (
    <>
      <SEO
        title={video?.title ? `${video.title} - Crown Law Library` : `Video ${videoId} - Crown Law Library`}
        description="Watch educational videos and engage with the community"
      />
      <div className="min-h-screen flex flex-col bg-background relative">
        <Header />
        
        {/* Loading state */}
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        
        {/* Auth overlay when user is not logged in */}
        {!user && !loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm p-4">
            <Card className="w-full max-w-lg border-2 border-primary/30 shadow-2xl bg-background/95">
              <CardContent className="pt-6 pb-6 sm:pt-8 sm:pb-8 px-6 text-center space-y-5">
                <div className="space-y-2.5">
                  <h2 className="text-2xl sm:text-3xl font-rockwell font-bold text-primary">
                    You need to be logged in to view this video
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground">
                    Access exclusive video content by signing in to your account
                  </p>
                  <p className="text-sm text-muted-foreground/80">
                    Don't have an account? Sign up for free!
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button 
                    onClick={() => navigate('/auth')}
                    className="w-full sm:flex-1"
                    size="default"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth?tab=signup')}
                    variant="outline"
                    className="w-full sm:flex-1"
                    size="default"
                  >
                    Join Free
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        <main className="flex-1 container mx-auto px-4 pt-24 pb-8">
          <div className={showSideLayout ? 'max-w-7xl mx-auto' : 'max-w-4xl mx-auto'}>
            {showSideLayout ? (
              /* Side-by-side layout for vertical videos on desktop */
              <ResizablePanelGroup direction="horizontal" className="gap-6">
                <ResizablePanel defaultSize={50} minSize={40}>
                  <div className="pr-3">
                    <VideoPlayer video={video} />
                    
                    {/* Video Title */}
                    {video?.title && (
                      <h1 className="text-2xl font-rockwell font-semibold text-primary mb-4 mt-4">
                        {video.title}
                      </h1>
                    )}
                    
                    <div className="mt-4">
                      <VideoShareButton videoId={videoId || ''} videoTitle={video?.title} />
                    </div>
                  </div>
                </ResizablePanel>
                
                <ResizableHandle withHandle />
                
                <ResizablePanel defaultSize={50} minSize={40}>
                  <div className="pl-3 h-[calc(100vh-200px)] overflow-y-auto">
                    <CommentsSection 
                      topLevelComments={topLevelComments}
                      totalComments={comments.length}
                      user={user}
                      newComment={newComment}
                      setNewComment={setNewComment}
                      isLoading={isLoading}
                      handleAddComment={handleAddComment}
                      navigate={navigate}
                      renderComment={renderComment}
                    />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            ) : (
              /* Standard stacked layout */
              <>
                <VideoPlayer video={video} />
                
                {/* Video Title */}
                {video?.title && (
                  <h1 className="text-2xl font-rockwell font-semibold text-primary mb-4">
                    {video.title}
                  </h1>
                )}
                
                <div className="mb-8">
                  <VideoShareButton videoId={videoId || ''} videoTitle={video?.title} />
                </div>

                <CommentsSection 
                  topLevelComments={topLevelComments}
                  totalComments={comments.length}
                  user={user}
                  newComment={newComment}
                  setNewComment={setNewComment}
                  isLoading={isLoading}
                  handleAddComment={handleAddComment}
                  navigate={navigate}
                  renderComment={renderComment}
                />
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default VideoDetailPage;
